import { useCallback, useEffect, useRef, useState } from 'react';
import { creditNotesApi } from '../services/creditNotesApi';
import {
    EMPTY_CREDIT_NOTE_FILTERS,
    type CreditNoteDocument,
    type CreditNoteFilters,
    type CreditNoteFreshness,
    type CreditNoteReasonCatalogEntry,
    type CreditNoteSummary,
    type CreditNoteVocabularies,
} from '../types/creditNotes';
import { monthsAgoIso, tomorrowIso } from '../utils/creditNoteFormat';

export const CREDIT_NOTE_PAGE_SIZE = 100;

/**
 * State for /credit-notes.
 *
 * SORTING AND PAGING ARE SERVER-SIDE, like every other queue page in this app — the endpoint
 * sorts and slices the whole window, so every setter resets to page 1: the row that was on page
 * two is somewhere else once the order changes.
 *
 * THE WINDOW DEFAULTS TO 12 MONTHS. The endpoint allows 24 and the measured cost of the full
 * 24-month, eight-company query is ~165 ms server-side, so the limit is about what a reviewer
 * can work through, not about what the database can carry.
 *
 * FILTERS ARE NOT PERSISTED. This is a work queue: what Stefan wants on opening it is the whole
 * period, not the narrow slice he left behind on Friday.
 */
const DEFAULT_FILTERS: CreditNoteFilters = {
    ...EMPTY_CREDIT_NOTE_FILTERS,
    dateFrom: monthsAgoIso(12),
    dateTo: tomorrowIso(),
};

const EMPTY_SUMMARY: CreditNoteSummary = {
    window: { dateFrom: DEFAULT_FILTERS.dateFrom, dateTo: DEFAULT_FILTERS.dateTo },
    companies: [],
    documentsInWindow: 0,
    documentsAfterFilters: 0,
    documentsWithoutItemLines: 0,
    documentsWithErpReasonCode: 0,
    documentsNotInLedger: 0,
    documentsFromSalesTransactionsOnly: 0,
    byCompany: {},
    byReason: {},
    queryMs: 0,
    ruleTiles: [],
    exclusions: [],
    skipReasonLabels: {},
    byAppReason: {},
    byReviewStatus: {},
    byRecourseStatus: {},
    documentsWithAppReason: 0,
    documentsWithReasonFromErp: 0,
    documentsWithReasonFromUser: 0,
    documentsReasonEditableAndEmpty: 0,
    unmappedErpReasons: { documents: 0, documentNos: [], values: [] },
    recourse: {
        openCases: 0, openBySupplier: 0, openByInsurance: 0, totalCases: 0,
        expectedByCurrency: {}, receivedByCurrency: {},
        overdueCases: 0, oldestOverdueDays: null, settledCases: 0, notPursuedCases: 0,
    },
    // Empty until the first response, like every other figure here — the Trends tab renders its
    // loading state rather than a chart of zeros that looks like a measured result.
    trends: {
        coverageByCompany: [], byReason: [], months: [],
        totals: { documents: 0, valueEur: 0, valueEurIsComplete: true },
    },
};

/**
 * Empty until the first response. The page never falls back to a locally written label: a
 * vocabulary invented in the browser can disagree with the CHECK constraint that decides what
 * the database will accept, and the disagreement would only surface on a save.
 */
const EMPTY_VOCABULARIES: CreditNoteVocabularies = {
    reviewStatuses: [], recourseStatuses: [], counterpartyTypes: [],
};

const EMPTY_FRESHNESS: CreditNoteFreshness = {
    lastEtlRunUtc: null, lastEtlStatus: null, etlSchedule: '', ageInDays: null, newestPostingDate: {},
};

export function useCreditNotes(enabled: boolean, initialFilters?: Partial<CreditNoteFilters>) {
    const [documents, setDocuments] = useState<CreditNoteDocument[]>([]);
    const [summary, setSummary] = useState<CreditNoteSummary>(EMPTY_SUMMARY);
    const [freshness, setFreshness] = useState<CreditNoteFreshness>(EMPTY_FRESHNESS);
    const [availableCompanies, setAvailableCompanies] = useState<string[]>([]);
    const [reasonCatalog, setReasonCatalog] = useState<CreditNoteReasonCatalogEntry[]>([]);
    const [vocabularies, setVocabularies] = useState<CreditNoteVocabularies>(EMPTY_VOCABULARIES);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [filters, setFiltersState] = useState<CreditNoteFilters>({ ...DEFAULT_FILTERS, ...initialFilters });
    const [sortBy, setSortBy] = useState<string>('postingDate');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);

    // Guards against a slow first request landing after a fast second one and overwriting it.
    const requestSeq = useRef(0);

    const load = useCallback(async () => {
        if (!enabled) return;
        const seq = ++requestSeq.current;
        setLoading(true);
        setError(null);
        try {
            const result = await creditNotesApi.list({
                page, limit: CREDIT_NOTE_PAGE_SIZE, filters, sortBy, sortOrder,
            });
            if (seq !== requestSeq.current) return;
            setDocuments(result.documents);
            setSummary(result.summary);
            setFreshness(result.freshness);
            setAvailableCompanies(result.availableCompanies);
            setReasonCatalog(result.reasonCatalog);
            setVocabularies(result.vocabularies ?? EMPTY_VOCABULARIES);
            setTotal(result.total);
        } catch (err) {
            if (seq !== requestSeq.current) return;
            setError(err instanceof Error ? err.message : 'Failed to load credit notes');
            setDocuments([]);
            setTotal(0);
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, [enabled, page, filters, sortBy, sortOrder]);

    useEffect(() => { void load(); }, [load]);

    const setFilters = useCallback((next: Partial<CreditNoteFilters>) => {
        setFiltersState(prev => ({ ...prev, ...next }));
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        // The period is not a filter in the same sense — clearing it would silently change the
        // population every tile is counted over, so it survives "clear filters".
        setFiltersState(prev => ({ ...DEFAULT_FILTERS, dateFrom: prev.dateFrom, dateTo: prev.dateTo }));
        setPage(1);
    }, []);

    /** Numeric and date columns read newest/largest first; text reads ascending. */
    const setSort = useCallback((key: string) => {
        setSortBy(prev => {
            if (prev === key) {
                setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
            } else {
                setSortOrder(key === 'postingDate' || key === 'amount' ? 'desc' : 'asc');
            }
            return key;
        });
        setPage(1);
    }, []);

    const activeFilterCount = [
        filters.search, filters.companyCode, filters.reasonCode, filters.appReasonCode,
        filters.reviewStatus, filters.recourseStatus, filters.rule,
    ].filter(Boolean).length;

    const totalPages = Math.max(1, Math.ceil(total / CREDIT_NOTE_PAGE_SIZE));

    return {
        documents, summary, freshness, availableCompanies, reasonCatalog, vocabularies, total,
        loading, error,
        filters, setFilters, clearFilters, activeFilterCount,
        sortBy, sortOrder, setSort,
        page, setPage, totalPages,
        reload: load,
    };
}
